import React, { useState } from 'react';
import UnifiedCoverageContainer from '../../pages/CoverageVerificationNew/components/UnifiedCoverageContainer';
import DrugSearchNew from '../../pages/DrugSearch/DrugSearchNew';
import { IconLockFilled, IconRefresh } from '@tabler/icons-react';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';
import subscriptionInactive from '../../assets/images/subscription-inactive.svg';
import ConsistentHeader from '../ConsistentHeader/ConsistentHeader';
import './UnifiedLanding.css';

/**
 * UnifiedLanding Component
 * Single page with Coverage Verification and Drug Search tabs
 * Now using UnifiedCoverageContainer for consistent layout
 */
const UnifiedLanding = () => {
  const [activeModule, setActiveModule] = useState('coverage');
  
  const { isActive: isSubscriptionActive } = useSubscription();
  
  const currentUserRole = useSelector(
    (state) => state.auth?.userAttributes['custom:role']
  );
  const organizationAttributes = useSelector(
    (state) => state.auth?.organization
  );
  const isOrgAdmin = currentUserRole === 'org_admin';

  const isFreeTrial = organizationAttributes?.status === 'free_trial';

  const renderSubscriptionAlert = () => {
    // Expired free trial — prompt user to activate a paid subscription
    if (isFreeTrial) {
      return (
        <div className="container py-2 text-center">
          <div className="mb-4">
            <img
              src={subscriptionInactive}
              alt="Free Trial Ended"
              style={{ maxWidth: '300px', margin: '0 auto' }}
            />
          </div>
          <h3 className="mb-3">Your free trial has ended</h3>
          <p className="mb-4 text-muted">
            Activate your subscription to continue accessing all features.
          </p>
          {isOrgAdmin && (
            <Link to="/subscription" className="btn btn-primary">
              Activate Subscription
            </Link>
          )}
          {!isOrgAdmin && (
            <p className="text-muted">
              Please contact your organization administrator to activate the subscription.
            </p>
          )}
        </div>
      );
    }

    if (isOrgAdmin) {
      return (
        <div className="container py-2 text-center">
          <div className="mb-4">
            <img
              src={subscriptionInactive}
              alt="Subscription Inactive"
              style={{ maxWidth: '300px', margin: '0 auto' }}
            />
          </div>
          <h3 className="mb-3">Your subscription has expired, but don't worry — it's easy to get back on track!</h3>
          <p className="mb-4 text-muted">
            Renew now to continue enjoying full access to all the features you love. We'd love to have you back!
          </p>
          <Link to="/settings/organization/billing" className="btn btn-primary">
            <IconRefresh size={18} className="mx-1" /> Renew Subscription
          </Link>
        </div>
      );
    }

    return (
      <div className="container py-2 text-center">
        <div className="mb-4">
          <img
            src={subscriptionInactive}
            alt="Subscription Inactive"
            style={{ maxWidth: '300px', margin: '0 auto' }}
          />
        </div>
        <h3 className="mb-3">Subscription Required</h3>
        <p className="mb-4 text-muted">
          Your organization's subscription is inactive. Please contact your
          organization administrator to renew the subscription.
        </p>
      </div>
    );
  };

  // If subscription is inactive, only show the expired message
  if (!isSubscriptionActive) {
    return (
      <div className="min-h-screen bg-white">
        <ConsistentHeader>
          <div className="flex gap-0.5 sm:gap-1 overflow-x-auto scrollbar-hide">
            <button
              type="button"
              disabled
              style={{ 
                border: 'none',
                borderBottom: '2px solid transparent',
                outline: 'none',
                boxShadow: 'none',
                cursor: 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              className="unified-landing-tab px-3 sm:px-4 md:px-6 py-3 font-medium transition-colors whitespace-nowrap text-sm md:text-base min-h-[44px] text-muted-foreground opacity-50"
              aria-label="Coverage Verification"
            >
              Coverage Verification
              <IconLockFilled size={16} className="ml-2" />
            </button>
            <button
              type="button"
              disabled
              style={{ 
                border: 'none',
                borderBottom: '2px solid transparent',
                outline: 'none',
                boxShadow: 'none',
                cursor: 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              className="unified-landing-tab px-3 sm:px-4 md:px-6 py-3 font-medium transition-colors whitespace-nowrap text-sm md:text-base min-h-[44px] text-muted-foreground opacity-50"
              aria-label="Drug Search"
            >
              Drug Search
              <IconLockFilled size={16} className="ml-2" />
            </button>
          </div>
        </ConsistentHeader>
        
        <main className="max-w-[1280px] mx-auto px-3 sm:px-4 md:px-4 lg:px-6 py-3 sm:py-4 md:py-6">
          {renderSubscriptionAlert()}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Top Navigation Bar with Tabs */}
      <ConsistentHeader>
        <div className="flex gap-0.5 sm:gap-1 overflow-x-auto scrollbar-hide">
          <button
            type="button"
            onClick={() => setActiveModule('coverage')}
            style={{ 
              border: 'none',
              borderBottom: activeModule === 'coverage' ? '2px solid' : '2px solid transparent',
              outline: 'none',
              boxShadow: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            className={`unified-landing-tab px-3 sm:px-4 md:px-6 py-3 font-medium transition-colors whitespace-nowrap text-sm md:text-base min-h-[44px] ${
              activeModule === 'coverage'
                ? 'text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            aria-label="Coverage Verification"
          >
            Coverage Verification
          </button>
          <button
            type="button"
            onClick={() => setActiveModule('drugSearch')}
            style={{ 
              border: 'none',
              borderBottom: activeModule === 'drugSearch' ? '2px solid' : '2px solid transparent',
              outline: 'none',
              boxShadow: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            className={`unified-landing-tab px-3 sm:px-4 md:px-6 py-3 font-medium transition-colors whitespace-nowrap text-sm md:text-base min-h-[44px] ${
              activeModule === 'drugSearch'
                ? 'text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            aria-label="Drug Search"
          >
            Drug Search
          </button>
        </div>
      </ConsistentHeader>

      {/* Main Content */}
      <main className="max-w-[1280px] mx-auto px-3 sm:px-4 md:px-4 lg:px-6 py-3 sm:py-4 md:py-6 space-y-3 sm:space-y-4 md:space-y-6">
        {activeModule === 'coverage' ? (
          <UnifiedCoverageContainer />
        ) : (
          <DrugSearchNew />
        )}
      </main>
    </div>
  );
};

export default UnifiedLanding;